import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Observable, Subject, combineLatest } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { PresidioImpersonationFacade } from 'src/app/extensions/presidio-demo/services/presidio-impersonation.facade';

import { AccountFacade } from 'ish-core/facades/account.facade';

import { PresidioShellUiFacade } from './presidio-shell-ui.facade';

/**
 * CUSTOMIZATION: Compact header account dropdown — My Account + Log out.
 * Mirrors contact-menu chrome behavior (exclusive overlay, click-outside, Escape).
 * MVP 8.2.2: while co-browsing, keep CSR name here; Viewing as shows the end user.
 */
@Component({
  selector: 'custom-account-menu',
  templateUrl: './custom-account-menu.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CustomAccountMenuComponent implements OnInit, OnDestroy {
  open = false;
  displayName$: Observable<string>;

  private destroy$ = new Subject<void>();

  constructor(
    private host: ElementRef<HTMLElement>,
    private shellUi: PresidioShellUiFacade,
    private accountFacade: AccountFacade,
    private impersonationFacade: PresidioImpersonationFacade,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.displayName$ = combineLatest([
      this.accountFacade.user$,
      this.impersonationFacade.isImpersonating$,
      this.impersonationFacade.actorDisplayName$,
    ]).pipe(
      map(([user, impersonating, actorName]) => {
        if (impersonating && actorName) {
          return actorName;
        }
        if (!user) {
          return '';
        }
        const full = `${user.firstName || ''} ${user.lastName || ''}`.trim();
        return full || user.login || '';
      })
    );

    this.shellUi.accountOpen$.pipe(takeUntil(this.destroy$)).subscribe(open => {
      this.open = open;
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggle(): void {
    this.shellUi.setAccountOpen(!this.open);
  }

  close(): void {
    this.shellUi.closeAccount();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.open && !this.host.nativeElement.contains(event.target as Node)) {
      this.close();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open) {
      this.close();
    }
  }
}
